import type { TFunction } from 'i18next';
import {
  BatteryCharging,
  Car,
  Disc,
  Droplet,
  Filter,
  Gauge,
  Lightbulb,
  List,
  Package,
  Snowflake,
  Wrench,
  Zap,
  type LucideIcon,
} from 'lucide-react';

/**
 * The counter's browse tabs (migration 148) — the layout shared by the till (CounterBrowse) and
 * its editor (Settings → Counter tabs). Entries are category CODES; names and counts come from
 * the catalogue (/catalog/browse-categories). A label is free text, or a built-in translated key
 * when the layout is the default one.
 */
export interface CounterColumn {
  title: string | null;
  titleKey: string | null;
  codes: string[];
}
export interface CounterTab {
  id: string;
  label: string | null;
  labelKey: string | null;
  icon: CounterIcon;
  columns: CounterColumn[];
}
export interface CounterLayout {
  tabs: CounterTab[];
}
export interface StoredCounterLayout {
  layout: CounterLayout;
  custom: boolean;
  updatedAt: string | null;
  updatedBy: string | null;
}

export const COUNTER_ICONS = {
  list: List,
  disc: Disc,
  droplet: Droplet,
  wrench: Wrench,
  battery: BatteryCharging,
  car: Car,
  package: Package,
  zap: Zap,
  lightbulb: Lightbulb,
  snowflake: Snowflake,
  filter: Filter,
  gauge: Gauge,
} satisfies Record<string, LucideIcon>;
export type CounterIcon = keyof typeof COUNTER_ICONS;

export const tabLabel = (t: TFunction, tab: CounterTab): string =>
  tab.label?.trim() || (tab.labelKey ? t(`counter.browseTabs.${tab.labelKey}`) : '');

export const columnTitle = (t: TFunction, col: CounterColumn): string =>
  col.title?.trim() || (col.titleKey ? t(`counter.browseTabs.col.${col.titleKey}`) : '');

export const layoutCodes = (layout: CounterLayout): string[] => [
  ...new Set(layout.tabs.flatMap((tab) => tab.columns.flatMap((c) => c.codes))),
];
